import { Image } from 'components/Image'
import { useEffect, useState, useContext, useMemo, useCallback } from 'react'
import { _debounce } from 'utils/shared'
import useMobileNavState from 'hooks/useMobileNavState'
import useIsSsr from 'hooks/useIsSsr'
import useBodyScrollLock from 'hooks/useBodyScrollLock'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'
import { Box, Button, AchieveTheme } from '@achieve/ascend'
import { Typography } from 'components/Typography'
import Container from '@mui/material/Container'
import { AchieveLink } from 'components/AchieveLink/AchieveLink'
import { SignInLink } from 'components/SignInLink'
import Navigation from 'components/Navigation'
import HeaderMobileActions from './HeaderMobileActions'
import HeaderCta from './HeaderCta'
import { handleTrackAndReactEvent } from 'utils/analytics'
import useLayoutMenuCondensed from 'hooks/useLayoutMenuCondensed'
import { X as CloseIcon, Plus, PhoneCall } from 'react-feather'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { PhoneDataContext } from 'providers/PhoneDataProvider'
import { MediaImageStandard } from 'components/Contentful'
import MenuIcon from 'components/Svgs/Menu.js'
import Close from 'components/Svgs/Close.js'
import { mainProductConfig } from 'constants/mainProductConfig'
import { FdrPage, FfnPage, AchievePage } from 'utils/environmentConfig'
import classNames from 'classnames'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { _get } from 'utils/shared'
import { Typography as OwnerTypography } from 'components/Contentful'
import { useGraduate } from 'providers/GraduateProvider'
import { WALL_OF_FAME_ROUTE } from 'constants/navigation'
import {
  STICKY_FOOTER_BLACKLIST,
  STICKY_FOOTER_BLACKLIST_FULLPATH,
} from 'components/Footer/FooterSticky/FooterSticky'
import { CurrentSvg } from 'components/CurrentSvg'
import { FDRHeader } from './FDRHeader/FDRHeader'
import { useAnyElementInViewport } from 'hooks/useAnyElementInViewport'
import { getSubdomain } from '@/utils/shared'
import { SIGN_IN_ROUTE } from 'constants/navigation'
import { useClientDashboardLoginOverlayTrigger } from '../../hooks/useClientDashboardLoginOverlayTrigger/useClientDashboardLoginOverlayTrigger.js'
import { useAnalyticsTrackEvent } from 'hooks/useAnalyticsTrackEvent'

const STICKY_HEADER_BLACKLIST = [
  '/',
  '/privacy/changes-to-program/',
  '/privacy/es/cambios-en-el-programa/',
]
const ACHIEVE_PRO_ROUTE = '/achieve-pro'

// Percentage of the window that needs to scroll for the mobile header to condense
const MOBILE_CONDENSED_THRESHOLD = 0.3

/**
 * Main site Header component
 * @param {{
 *  mainNavigation: object
 *  disabledRoutes: []
 * }} props
 */
export default function Header({
  disabledElements,
  disabledRoutes,
  mainNavigation,
  logoEvent,
  signInLinkUrl,
  signInLinkText,
  signInEvent,
  linkUrl,
  linkText,
  headerCta = true,
  linkEvent,
  showNav = true,
  showSignIn = true,
  customPhoneData = null,
  partnerLogo,
  logoLink = true,
  showProgressPage = false,
  siteEnv,
  footerSticky,
  showPhoneNumber = false,
  showDebtConsultantCta = false,
  stickyHeaderSettings = {},
  isMobileUA = false,
}) {
  const [, setLayoutMenuCondensed] = useLayoutMenuCondensed()
  const { fireAndForget } = useTriggerEvents()
  const [mobileNavOpen, setMobileNavOpen] = useMobileNavState()
  const [condensed, setCondensed] = useState(false)
  const [mobileCondensed, setMobileCondensed] = useState(false)
  const isSsr = useIsSsr()
  const mqHookResults = useViewportSmallerThan(BREAKPOINTS.lg)
  const [buttonCall, setButtonCall] = useState(false)
  const [backLink, setBackLink] = useState('/')
  const [isHomePath, setIsHomePath] = useState(false)
  let phoneData = useContext(PhoneDataContext)
  const { analyticsTrackEvent } = useAnalyticsTrackEvent()

  const phoneIconUrl = '/multi-site-assets/icon-phone.svg'
  const phoneIconBlueUrl = '/multi-site-assets/phone-blue.svg'
  const phoneCallIconUrl = '/multi-site-assets/phone-call.svg'

  const [progressPage, setProgressPage] = useState(0)
  const { dynamicPathname, fullAsPath, params } = useAppRouter()
  const tp_subtitle = _get(footerSticky, 'card.subtitle')
  const tp_totalRating = _get(footerSticky, 'card.totalRating')
  const tp_totalReviews = _get(footerSticky, 'card.totalReviews')
  const tp_text = tp_totalReviews
    ? `${tp_totalRating}/5 from ${tp_totalReviews} reviews`
    : tp_subtitle
  const tp_image = _get(footerSticky, 'card.image')

  const { graduate } = useGraduate()

  // 1. don't display if the active route is in the blacklist
  // 2. don't display if the active route is the Wall of Fame graduate tokenized route
  // 3. don't display if the active full path is in the full path blacklist (for query params cases)
  const stickyFooterDisabled =
    [...STICKY_FOOTER_BLACKLIST, ...(graduate ? [WALL_OF_FAME_ROUTE] : [])].includes(
      dynamicPathname
    ) || STICKY_FOOTER_BLACKLIST_FULLPATH.includes(fullAsPath)

  const showStickyFooter = !stickyFooterDisabled
  /**
   * The below logic is used specifically for home-equity-loans-mp. AHL Direct Mail campaign
   * requires phone number to be based on utm_source and utm_content. Phone number data is
   * fetched from pageConfig object in Home Equity Loan MP contentful page entry.
   * Implemented by PXP team.
   * Contact: Shreyas Dorle, Brandon Chapman
   */
  if (customPhoneData) {
    const { utm_source, utm_content, ahl_dm_creatives } = customPhoneData ?? {}
    const {
      informed_delivery,
      digital,
      default_values,
      utm_source: ahl_dm_utm_source,
    } = ahl_dm_creatives ?? {}
    if (utm_source === ahl_dm_utm_source) {
      if (utm_content?.toUpperCase() === informed_delivery?.utm_content) {
        phoneData.phoneNumber = informed_delivery?.phone
      } else if (utm_content?.toUpperCase() === digital?.utm_content) {
        phoneData.phoneNumber = digital?.phone
      } else {
        phoneData.phoneNumber = default_values?.phone
      }
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const protocol = window.location.protocol
      const host = window.location.host
      setIsHomePath(window.location.pathname === '/')
      if (host) {
        if (getSubdomain(host, 'legal')) {
          const url = `${protocol}//${host}`.replaceAll('legal.', '').replaceAll('legal-', '')
          setBackLink(url)
        } else {
          setBackLink('/')
        }
      }
    }
  }, [])

  // Default to mobile first before initial render if the user agent matches a mobile device
  const isMobile = isSsr ? isMobileUA : mqHookResults

  const debounceOptions = useMemo(
    () => ({
      leading: true,
      trailing: false,
    }),
    []
  )

  // showStickyHeaderOnLeave must be an array of html IDs
  const onViewport = useAnyElementInViewport(
    stickyHeaderSettings?.showStickyHeaderOnLeave,
    siteEnv === FdrPage && !isMobile ? 120 : 80
  )

  const setMobileCondensedTrue = useMemo(
    () => _debounce(() => setMobileCondensed(true), 100, debounceOptions),
    [debounceOptions]
  )
  const setMobileCondensedFalse = useMemo(
    () => _debounce(() => setMobileCondensed(false), 100, debounceOptions),
    [debounceOptions]
  )
  const setCondensedTrue = useMemo(
    () => _debounce(() => setCondensed(true), 100, debounceOptions),
    [debounceOptions]
  )
  const setCondensedFalse = useMemo(
    () => _debounce(() => setCondensed(false), 100, debounceOptions),
    [debounceOptions]
  )

  const isDebtConsolidation = params.slug === 'debt-consolidation'

  useEffect(() => {
    return () => {
      setMobileCondensedTrue.cancel()
      setMobileCondensedFalse.cancel()
      setCondensedTrue.cancel()
      setCondensedFalse.cancel()
    }
  }, [setMobileCondensedTrue, setMobileCondensedFalse, setCondensedTrue, setCondensedFalse])

  /**
   * Set the initial condensed state based on window scroll position
   */
  useEffect(() => {
    if (isSsr) {
      // no window to measure during SSR
      return
    }

    onScroll()
    // This effect is only intended to run on the first browser (not SSR) render
  }, [isSsr]) /* eslint-disable-line react-hooks/exhaustive-deps */

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleScroll = _debounce(() => {
      onScroll()
    }, 30)
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [onScroll])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function onScroll() {
    if (typeof window === 'undefined') return

    if (showProgressPage && condensed) {
      const currentScroll = window.scrollY
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
      const scrollPercentage = (currentScroll / scrollHeight) * 100
      setProgressPage(scrollPercentage)
    }

    if (isMobile) {
      return handleMobileScroll()
    }
    return handleDesktopScroll()
  }

  /**
   * Enable the condensed mobile header when the page is scrolled passed the threshold
   */
  function handleMobileScroll() {
    const currentScroll = window.scrollY
    const nextMobileCondensed = currentScroll > window.innerHeight * MOBILE_CONDENSED_THRESHOLD

    if (nextMobileCondensed === mobileCondensed && !onViewport) {
      return
    }

    if (nextMobileCondensed && !onViewport) {
      return setMobileCondensedTrue()
    }

    return setMobileCondensedFalse()
  }

  /**
   * Enable the condensed desktop header when the page is scrolled any distance from the top
   */
  function handleDesktopScroll() {
    const currentScroll = window.scrollY
    const nextCondensed = Boolean(currentScroll)

    if (nextCondensed === condensed && !onViewport) {
      return
    }

    if (nextCondensed && !onViewport) {
      return setCondensedTrue()
    }

    return setCondensedFalse()
  }

  // FDR relies only on useBodyScrollLock below, which keeps the page in place behind the open nav
  const lockBodyWithInlineStyles = siteEnv !== FdrPage

  /*
    Prevent body scroll when the mobile navigation menu is open
  */
  useEffect(() => {
    const body = document.querySelector('body')

    if (!body || !lockBodyWithInlineStyles) {
      return
    }

    if (isMobile && mobileNavOpen) {
      // Prevent the body scrolling under the open mobile nav menu by fixing the body element,
      // setting the height to the viewport height and setting the overflow to hidden
      body.style.position = 'fixed'
      body.style.height = '100vh'
      body.style.width = '100vw'
      body.style.overflow = 'hidden'
    } else {
      // Reset the body css to scroll and overflow normally.
      body.style.position = 'initial'
      body.style.height = 'initial'
      body.style.width = 'initial'
      body.style.overflow = 'initial'
    }

    // Cleanup: always restore body styles on unmount or before the next effect run
    return () => {
      body.style.position = 'initial'
      body.style.height = 'initial'
      body.style.width = 'initial'
      body.style.overflow = 'initial'
    }
  }, [isMobile, mobileNavOpen, lockBodyWithInlineStyles])
  useBodyScrollLock(isMobile && mobileNavOpen, { preserveScrollPosition: true })

  const handleCloseClick = useCallback(
    (event, track) => {
      fireAndForget({
        event_type: track?.event_type,
        ...handleTrackAndReactEvent(event, track),
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      !isSsr && window.scrollTo({ top: 0, behavior: 'instant' })
      setMobileNavOpen(false)
    },
    [fireAndForget, isSsr, setMobileNavOpen]
  )

  const handleOpenClick = useCallback(
    (event, track) => {
      fireAndForget({
        event_type: track?.event_type,
        ...handleTrackAndReactEvent(event, track),
      })
      setMobileNavOpen(true)
    },
    [fireAndForget, setMobileNavOpen]
  )

  useEffect(() => {
    setLayoutMenuCondensed(isMobile ? mobileCondensed : condensed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, condensed, mobileCondensed])

  const phoneHref = phoneData?.phoneNumber ? `tel:${phoneData.phoneNumber}` : undefined

  const callToAssociatedNumber = useCallback(() => {
    if (phoneData?.phoneNumber) window.open(`tel:${phoneData.phoneNumber}`, '_self')
  }, [phoneData?.phoneNumber])

  const handleCall = useCallback(() => {
    if (isMobile) callToAssociatedNumber()
    else setButtonCall((prev) => !prev)
  }, [isMobile, callToAssociatedNumber])

  const handleMobileNavClose = useCallback(() => {
    setMobileNavOpen(false)
  }, [setMobileNavOpen])

  const handleNavOpen = useCallback(
    (e) => {
      handleOpenClick(e, {
        nav_link_section: 'Toggle menu',
        click_type: 'Button Click',
        click_id: 'open button',
      })
    },
    [handleOpenClick]
  )

  const handleNavClose = useCallback(
    (e) => {
      handleCloseClick(e, {
        nav_link_section: 'Toggle menu',
        click_type: 'Button Click',
        click_id: 'close button',
      })
    },
    [handleCloseClick]
  )

  const isAchievePro = dynamicPathname?.startsWith(ACHIEVE_PRO_ROUTE) && siteEnv === AchievePage

  const getSubNav = () => {
    if (isAchievePro) {
      return (
        <div className="flex flex-col gap-3 lg:hidden">
          <AchieveLink
            href="/achieve-pro/contact-us"
            className="w-full"
            track={{
              nav_link_section: 'Header Achieve Pro Mobile',
              click_id: 'partner-with-us',
              click_text: linkText || 'Partner with us',
              track_event: 'global_header',
              event_action: 'button_click',
              event_type: 'button_click',
            }}
          >
            <Button color="primary" variant="contained" className="w-full **:text-white!">
              <Typography className="font-extrabold!" variant="labelLg" fontWeight="extrabold">
                {linkText || 'Partner with us'}
              </Typography>
            </Button>
          </AchieveLink>
          <AchieveLink
            href="https://account.achievepro.com/portal"
            className="w-full"
            track={{
              nav_link_section: 'Header Achieve Pro Mobile',
              click_id: 'partner-login',
              click_text: signInLinkText || 'Partner Login',
              track_event: 'global_header',
              event_action: 'button_click',
              event_type: 'button_click',
            }}
          >
            <Button
              color="secondary"
              variant="contained"
              className="w-full border-[#1D252F]! **:text-[#1D252F]!"
            >
              <Typography
                variant="labelLg"
                className="font-ultramarine text-[18px] font-bold leading-[120%] tracking-[0.36px]"
              >
                {signInLinkText || 'Partner Login'}
              </Typography>
            </Button>
          </AchieveLink>
          <a
            href="tel:+18336491847"
            className="w-full flex items-center justify-center gap-2 no-underline py-2 text-[#1D252F] font-ultramarine text-[18px] font-bold leading-[120%] tracking-[0.36px]"
          >
            Call us
            <PhoneCall size={16} />
          </a>
        </div>
      )
    }
    if (!isMobile || !phoneData?.phoneNumber) return ''
    return (
      <Box
        className={classNames(
          siteEnv === AchievePage
            ? 'flex flex-col gap-4 items-center lg:hidden'
            : 'bg-neutral-50 flex flex-col gap-4 items-center p-6 lg:hidden'
        )}
      >
        {siteEnv === AchievePage ? (
          <>
            <MediaImageStandard
              className="min-h-[72px]! mb-5"
              alt="smarter debt starts here"
              height={72}
              content={{ url: '/smarter-debt.svg' }}
              width={338}
              objectFit="contain"
              quality={100}
            />
            <AchieveLink
              className="w-full"
              href={linkUrl}
              track={{
                ...{
                  list_name: 'Header Nav Main CTA Button',
                  click_id: 'Get Started',
                  click_text: `${
                    mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
                  } | Get Started`,
                  nav_link_section: 'Header',
                  track_event: 'global_header',
                  event_action: 'button_click',
                  event_type: 'button_click',
                },
                ...linkEvent,
              }}
            >
              <Button color="primary" variant="contained" className="w-full">
                <Typography className="font-extrabold!" variant="labelLg" fontWeight="extrabold">
                  {linkText ? linkText : `Get Started`}
                </Typography>
              </Button>
            </AchieveLink>
            <AchieveLink
              color="primary"
              href={signInLinkUrl ? signInLinkUrl : SIGN_IN_ROUTE}
              className="w-full"
              track={{
                ...{
                  list_name: 'Header Nav Sign in Link',
                  click_id: 'Sign in',
                  click_text: `${
                    mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
                  } | Login`,
                  nav_link_section: 'Header',
                  track_event: 'global_header',
                  event_action: 'link_click',
                  event_type: 'link_click',
                },
                ...signInEvent,
              }}
              variant="bodySm"
            >
              <Button color="primary" variant="contained" className="bg-white text-blue-600 w-full">
                <Typography className="font-extrabold!" variant="labelLg" fontWeight="extrabold">
                  Login
                </Typography>
              </Button>
            </AchieveLink>
            <AchieveLink
              color="primary"
              href={phoneHref}
              track={{
                list_name: 'PHONE CALL CLICK HEADER',
                click_id: phoneData?.phoneNumber,
                click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']} | ${
                  phoneData?.phoneNumber
                }`,
                click_url: phoneData?.phoneNumber,
                click_type: 'Phone Click',
                event_action: 'button_click',
                nav_link_section: 'Header',
                track_event: 'global_header',
              }}
              className="w-full"
            >
              <Button
                component="span"
                className="w-full text-white hover:bg-transparent [&_img]:object-contain [&_img]:pl-1"
                size="small"
                endIcon={getMobileIcon()}
                data-testid="header-subnav-phone-button"
              >
                <Typography className="font-extrabold!" variant="labelLg" fontWeight="extrabold">
                  Call Us
                </Typography>
              </Button>
            </AchieveLink>
          </>
        ) : (
          <>
            <Typography
              variant="bodyLg"
              fontWeight="regular"
              data-testid="header-subnav-phone-copy"
            >
              {phoneData?.phoneCopy}
            </Typography>
            <AchieveLink
              className={''}
              href={phoneHref}
              track={{
                list_name: 'PHONE CALL CLICK HEADER',
                click_id: phoneData?.phoneNumber,
                click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']} | ${
                  phoneData?.phoneNumber
                }`,
                click_url: phoneData?.phoneNumber,
                click_type: 'Phone Click',
                event_action: 'button_click',
                nav_link_section: 'Header',
                track_event: 'global_header',
              }}
            >
              <Button
                component="span"
                variant="outlined"
                size="small"
                startIcon={getMobileIcon()}
                data-testid="header-subnav-phone-button"
              >
                {phoneData?.phoneNumber}
              </Button>
            </AchieveLink>
          </>
        )}
      </Box>
    )
  }

  const getMobileIcon = () => {
    if (siteEnv === FfnPage || (siteEnv === AchievePage && isDebtConsolidation)) {
      return (
        <MediaImageStandard
          className={classNames(
            'object-none',
            'group-data-[product="website-ffn"]:object-contain',
            'group-data-[slug="debt-consolidation"]:group-data-[product="website-ffn"]:object-contain'
          )}
          alt={'phone icon'}
          height={20}
          content={{ url: phoneIconBlueUrl }}
          width={20}
          layout="fill"
          objectFit="contain"
        />
      )
    }

    return (
      <Box position={'relative'}>
        <MediaImageStandard
          className={classNames(
            'object-none',
            'group-data-[product="website-ffn"]:object-contain',
            'group-data-[slug="debt-consolidation"]:group-data-[product="website-ffn"]:object-contain'
          )}
          alt={'phone icon'}
          height={condensed ? 20 : 24}
          content={{ url: siteEnv === AchievePage ? phoneCallIconUrl : phoneIconUrl }}
          width={condensed ? 20 : 24}
          layout="fill"
          objectFit="contain"
        />
      </Box>
    )
  }

  const getCallDesktop = () => {
    return (
      <Box className="flex flex-row gap-2 cursor-pointer">
        <MediaImageStandard
          classNameContainer="relative"
          alt={'phone icon'}
          height={24}
          content={{ url: phoneIconBlueUrl }}
          width={24}
          layout="fill"
        />
        <Typography variant="bodyLg" fontWeight="regular" component="p">
          {phoneData?.phoneNumber}
        </Typography>
      </Box>
    )
  }

  const getDebtConsultantCTA = () => {
    return (
      <div className="hidden justify-end lg:flex">
        <div className="flex items-center">
          <OwnerTypography
            className="text-[12px] leading-[140%]"
            fontWeight="regular"
            content="Talk to a certified debt consultant"
          />
          <AchieveLink
            href={phoneHref}
            track={{
              list_name: 'CALL STICKY HEADER',
              click_id: phoneData?.phoneNumber,
              click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainName']} | ${
                phoneData?.phoneNumber
              }`,
              click_url: phoneData?.phoneNumber,
              click_type: 'Phone Click',
              event_action: 'button_click',
              nav_link_section: 'Above Header',
              track_event: 'phone_click',
            }}
            data-testid="debt-consultant-cta"
          >
            <Button
              component="span"
              className="hover:bg-transparent text-content-primary"
              variant="text"
              size="small"
              onClick={callToAssociatedNumber}
            >
              <OwnerTypography
                variant="displayXxs"
                fontWeight="bold"
                content={phoneData?.phoneNumber}
              />
            </Button>
          </AchieveLink>
        </div>
      </div>
    )
  }

  const mobileLogoStyles = classNames(
    '[&_img]:w-[118px] [&_img]:h-[inherit] [&_img]:min-w-[inherit] [&_img]:max-w-[inherit]',
    isAchievePro
      ? 'grow-0 shrink-0 h-[30px] absolute transition-[400ms_cubic-bezier(0,0,0.2,1)] w-12 overflow-hidden lg:hidden'
      : 'grow-0 shrink-0 h-[27px] absolute transition-[400ms_cubic-bezier(0,0,0.2,1)] w-9 overflow-hidden lg:hidden',
    siteEnv !== AchievePage &&
      'group-data-[mobile-nav-open=false]:group-data-[mobile-condensed=false]:translate-y-[-90px]',
    'group-data-[product="website-tenant-2"]:h-[30px] group-data-[product="website-tenant-2"]:w-[70px]',
    'group-data-[product="website-ffn"]:h-[30px] group-data-[product="website-ffn"]:w-[70px]',
    'group-data-[product="website-fdr"]:h-auto group-data-[product="website-fdr"]:w-full group-data-[product="website-fdr"]:max-w-[70px] group-data-[product="website-fdr"]:max-h-[30px] group-data-[product="website-fdr"]:flex group-data-[product="website-fdr"]:items-center group-data-[product="website-fdr"]:justify-center',
    '[&_img]:group-data-[product="website-fdr"]:w-full [&_img]:group-data-[product="website-fdr"]:h-full [&_img]:group-data-[product="website-fdr"]:max-h-[30px] [&_img]:group-data-[product="website-fdr"]:object-contain',
    '[&_svg]:group-data-[product="website-fdr"]:w-full [&_svg]:group-data-[product="website-fdr"]:h-full [&_svg]:group-data-[product="website-fdr"]:max-h-[30px] [&_svg]:group-data-[product="website-fdr"]:object-contain',
    'group-data-[product="website-wfs"]:h-10 group-data-[product="website-wfs"]:w-[120px] group-data-[product="website-wfs"]:overflow-visible',
    siteEnv === AchievePage && 'max-lg:group-data-[mobile-nav-open=true]:hidden'
  )

  const mobileSVGStyles = 'w-full h-auto min-w-[118px]'

  const desktopLogoStyles = classNames(
    isAchievePro && 'group-data-[mobile-nav-open=true]:bg-red bg-white z-10',
    'h-[44px] w-[119px] flex grow-0 shrink-0 relative transition-[400ms_cubic-bezier(0,0,0.2,1)] lg:w-[180px] lg:translate-none lg:[transform:translateY(0)]',
    // 'group-data-[mobile-nav-open="true"]:translate-y-[-90px]',
    'group-data-[mobile-condensed="true"]:translate-y-[-90px]',
    'group-data-[product="website-ffn"]:h-10',
    'group-data-[slug="debt-consolidation"]:group-data-[product="achieve"]:h-10',
    'group-data-[product="website-wfs"]:h-[62px] [&_img]:group-data-[product="website-wfs"]:max-h-full',
    'group-data-[mobile-condensed=false]:group-data-[product="website-wfs"]:pt-0',
    'group-data-[mobile-condensed=false]:group-data-[product="website-wfs"]:h-[62px] group-data-[mobile-condensed=false]:group-data-[product="website-wfs"]:pt-2',
    'lg:group-data-[mobile-condensed=true]:translate-none',
    'lg:group-data-[mobile-nav-open=true]:translate-none',
    'lg:group-data-[condensed=true]:w-[117px]',
    'lg:group-data-[product="website-tenant-2"]:h-10 lg:group-data-[product="website-tenant-2"]:w-[90px]',
    'lg:group-data-[product="website-fdr"]:h-11 lg:group-data-[product="website-fdr"]:w-[90px]',
    'lg:group-data-[product="website-ffn"]:h-10 lg:group-data-[product="website-ffn"]:w-[90px]',
    'lg:group-data-[product="website-wfs"]:h-[87px] lg:group-data-[product="website-wfs"]:w-auto',
    'lg:[&_img]:group-data-[product="website-wfs"]:h-[87px] lg:[&_img]:group-data-[product="website-wfs"]:w-auto',
    'lg:[&_img]:group-data-[condensed=true]:group-data-[product="website-wfs"]:h-[68px] lg:[&_img]:group-data-[condensed=true]:group-data-[product="website-wfs"]:pt-4',
    !isAchievePro &&
      'max-lg:[&_svg_path]:group-data-[mobile-nav-open=true]:fill-white! max-lg:[&_svg_rect]:group-data-[mobile-nav-open=true]:fill-white!'
  )

  const AchieveLogo = ({ mobile, logoLink }) => {
    const homeLinkLabel = `${
      mainProductConfig[siteEnv]?.MainName || mainProductConfig.achieve.MainName
    } home`

    /*Combination as follows:
      Mobile with Link
      Mobile without Link
      Desktop with Link
      Desktop without Link
    */
    return mobile ? (
      logoLink ? (
        <AchieveLink
          track={{
            list_name: 'Header Logo',
            click_id: `2022_${mainProductConfig[siteEnv || 'achieve']?.['MainName']?.replace(
              / /g,
              '_'
            )}_Logo_RGB`,
            click_text: `${
              mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
            } | Header logo`,
            nav_link_section: 'Header logo',
            track_event: 'global_header',
            event_action: 'link_click',
            event_type: 'link_click',
          }}
          href={isAchievePro ? ACHIEVE_PRO_ROUTE : backLink}
          withNextLink
          id="mobile-header-logo"
          className={mobileLogoStyles}
          aria-label={homeLinkLabel}
          data-testid={`${String(
            mainProductConfig[siteEnv || 'achieve']?.['MainName']
          )?.toLowerCase()}-header-mobile-logo`}
          onClick={handleMobileNavClose}
        >
          <CurrentSvg siteEnv={siteEnv} className={mobileSVGStyles} route={dynamicPathname} />
        </AchieveLink>
      ) : (
        <div id="mobile-header-logo" className={mobileLogoStyles}>
          <CurrentSvg siteEnv={siteEnv} className={mobileSVGStyles} route={dynamicPathname} />
        </div>
      )
    ) : logoLink ? (
      <AchieveLink
        track={{
          ...{
            list_name: 'Header Logo',
            click_id: `${mainProductConfig[siteEnv || 'achieve']?.['HeaderLogo']}`,
            click_text: `${
              mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
            } | Header logo`,
            nav_link_section: 'Header logo',
            track_event: 'global_header',
            event_action: 'link_click',
            event_type: 'link_click',
          },
          ...logoEvent,
        }}
        href={isAchievePro ? ACHIEVE_PRO_ROUTE : backLink}
        withNextLink
        className={desktopLogoStyles}
        aria-label={homeLinkLabel}
        data-testid={`${String(
          mainProductConfig[siteEnv || 'achieve']?.['MainName']
        )?.toLowerCase()}-header-logo`}
      >
        <CurrentSvg siteEnv={siteEnv} route={dynamicPathname} />
      </AchieveLink>
    ) : (
      <div className={desktopLogoStyles}>
        <CurrentSvg siteEnv={siteEnv} route={dynamicPathname} />
      </div>
    )
  }

  const PartnerLogo = ({ src }) => {
    return (
      <div className={'w-[120px] h-full lg:w-[180px] lg:h-full'}>
        <Image
          src={src}
          alt={`${mainProductConfig[siteEnv || 'achieve']?.['MainName']} partner logo`}
          className={'h-full w-full'}
          height="60"
          width="180"
          fetchPriority="high"
          preload={true}
        />
      </div>
    )
  }

  const AchieveLogoMobile = useMemo(
    () => <AchieveLogo mobile={true} logoLink={logoLink} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [logoLink, backLink]
  )

  const AchieveLogoDesktop = useMemo(
    () => <AchieveLogo mobile={false} logoLink={logoLink} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [logoLink, backLink]
  )

  const callUsButton = useMemo(() => {
    if (siteEnv === AchievePage) {
      return (
        <Button
          component="span"
          color="secondary"
          variant="contained"
          className="w-full flex flex-row gap-2"
          size="small"
          onClick={callToAssociatedNumber}
        >
          <MediaImageStandard
            className="object-contain"
            classNameContainer="relative"
            alt={'phone icon'}
            height={20}
            content={{ url: phoneIconUrl }}
            width={20}
            layout="fill"
            objectFit="contain"
          />
          <OwnerTypography variant="displayXs" fontWeight="bold" content={phoneData?.phoneNumber} />
        </Button>
      )
    }
    return (
      <Button
        component="span"
        color="secondary"
        variant="contained"
        className="w-full flex flex-row gap-2"
        size="small"
        onClick={callToAssociatedNumber}
      >
        <OwnerTypography variant="displayXs" fontWeight="bold" content="Call Us" />
      </Button>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteEnv, phoneData?.phoneNumber, callToAssociatedNumber])

  const { openOverlayInsteadOfNavigating } = useClientDashboardLoginOverlayTrigger()
  const handleButtonClick = useCallback(
    //todo changed as CTA button, but destination url is https://fdr-dev.programdashboard.com/login
    (href, name, isLeadCta) => {
      analyticsTrackEvent({
        nav_link_section: 'Header',
        click_type: 'Button Click',
        click_id: name,
        click_url: href,
        click_text: `FDR-Web | ${name}`,
        track_event: 'global_header',
        event_type: isLeadCta ? 'conversion' : 'track',
      })
    },
    [analyticsTrackEvent]
  )

  if (siteEnv === FdrPage && condensed && !STICKY_HEADER_BLACKLIST.includes(dynamicPathname)) {
    return (
      <FDRHeader
        siteEnv={siteEnv}
        tp_text={tp_text}
        tp_image={tp_image}
        linkUrl={linkUrl}
        phoneData={phoneData}
        mainProductConfig={mainProductConfig}
        backLink={backLink}
        logoEvent={logoEvent}
        logoLink={logoLink}
      />
    )
  }

  const shouldShowPhoneNumber = isMobile && phoneData?.phoneNumber && showPhoneNumber

  return (
    <>
      <header
        className={classNames(
          'bg-white shadow-[0_0.5px_3px_rgb(16_24_40/9%),0_1px_1px_rgb(16_24_40/3%)]  w-full z-15 lg:shadow-none',
          { fixed: !(isHomePath && siteEnv === FdrPage) },
          // FDR: keeps it in view while the body is offset by the mobile nav scroll lock
          { 'top-0': siteEnv === FdrPage && !isHomePath },
          { relative: isHomePath && siteEnv === FdrPage },
          'lg:data-[condensed=true]:shadow-[0_6px_40px_-4px_rgb(16_24_40/11%),0_9px_12px_-3px_rgb(16_24_40/5%)]',
          'lg:data-[product="website-tenant-2"]:data-[selected=true]:after:content-[inherit]',
          'lg:data-[product="website-fdr"]:data-[selected=true]:after:content-[inherit]',
          'lg:data-[product="website-ffn"]:data-[selected=true]:after:content-[inherit]',
          'lg:data-[product="website-tenant-2"]:data-[nav="item-title"]:text-[13px] lg:data-[product="website-tenant-2"]:data-[nav="item-title"]:font-thin lg:data-[product="website-tenant-2"]:data-[nav="item-title"]:text-center lg:data-[product="website-tenant-2"]:data-[nav="item-title"]:block',
          'lg:data-[product="website-fdr"]:data-[nav="item-title"]:text-[13px] lg:data-[product="website-fdr"]:data-[nav="item-title"]:font-thin lg:data-[product="website-fdr"]:data-[nav="item-title"]:text-center lg:data-[product="website-fdr"]:data-[nav="item-title"]:block',
          'lg:data-[product="website-ffn"]:data-[nav="item-title"]:text-[13px] lg:data-[product="website-ffn"]:data-[nav="item-title"]:font-thin lg:data-[product="website-ffn"]:data-[nav="item-title"]:text-center lg:data-[product="website-ffn"]:data-[nav="item-title"]:block',
          'lg:[&_div]:data-[nav="sub-nav"]:data-[product="website-tenant-2"]:text-[13px] lg:[&_div]:data-[nav="sub-nav"]:data-[product="website-tenant-2"]:font-thin',
          'lg:[&_div]:data-[nav="sub-nav"]:data-[product="website-fdr"]:text-[13px] lg:[&_div]:data-[nav="sub-nav"]:data-[product="website-fdr"]:font-thin',
          'lg:[&_div]:data-[nav="sub-nav"]:data-[product="website-ffn"]:text-[13px] lg:[&_div]:data-[nav="sub-nav"]:data-[product="website-ffn"]:font-thin',
          'lg:data-[product="website-tenant-2"]:data-[nav="sub-nav"]:px-4 lg:data-[product="website-tenant-2"]:data-[nav="sub-nav"]:py-[6.4px]',
          'lg:data-[product="website-fdr"]:data-[nav="sub-nav"]:px-4 lg:data-[product="website-fdr"]:data-[nav="sub-nav"]:py-[6.4px]',
          'lg:data-[product="website-ffn"]:data-[nav="sub-nav"]:px-4 lg:data-[product="website-ffn"]:data-[nav="sub-nav"]:py-[6.4px]',
          'lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:translate-x-0 lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:translate-y-[69px]',
          'lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:translate-x-0 lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:translate-y-[69px] lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:[overflow:initial]',
          'lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:translate-x-0 lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:translate-y-[69px] lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:[overflow:initial]',
          'lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:translate-x-0 lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:translate-y-[69px] lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:[overflow:initial]',
          'lg:data-[product="website-tenant-2"]:data-[condensed=true]:data-[nav="sub-nav-container"]:translate-x-0 lg:data-[product="website-tenant-2"]:data-[condensed=true]:data-[nav="sub-nav-container"]:translate-y-[55px]',
          'lg:data-[product="website-fdr"]:data-[condensed=true]:data-[nav="sub-nav-container"]:translate-x-0 lg:data-[product="website-fdr"]:data-[condensed=true]:data-[nav="sub-nav-container"]:translate-y-[55px]',
          'lg:data-[product="website-ffn"]:data-[condensed=true]:data-[nav="sub-nav-container"]:translate-x-0 lg:data-[product="website-ffn"]:data-[condensed=true]:data-[nav="sub-nav-container"]:translate-y-[55px]',
          'lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:bg-white lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:shadow-[-2.5px_-2.5px_2px_0_rgba(0_0_0/10%),inset_0_1px_3px_0_transparent] lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:content-[""] lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:h-[0.9285rem] lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:absolute lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:top-[-0.4rem] lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:rotate-45 lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:w-[0.9285rem] lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:left-5 lg:data-[product="website-tenant-2"]:data-[nav="sub-nav-container"]:after:right-0',
          'lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:bg-white lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:shadow-[-2.5px_-2.5px_2px_0_rgba(0_0_0/10%),inset_0_1px_3px_0_transparent] lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:content-[""] lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:h-[0.9285rem] lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:absolute lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:top-[-0.4rem] lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:rotate-45 lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:w-[0.9285rem] lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:left-5 lg:data-[product="website-fdr"]:data-[nav="sub-nav-container"]:after:right-0',
          'lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:bg-white lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:shadow-[-2.5px_-2.5px_2px_0_rgba(0_0_0/10%),inset_0_1px_3px_0_transparent] lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:content-[""] lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:h-[0.9285rem] lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:absolute lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:top-[-0.4rem] lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:rotate-45 lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:w-[0.9285rem] lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:left-5 lg:data-[product="website-ffn"]:data-[nav="sub-nav-container"]:after:right-0',
          'lg:[&_li:first-child]:data-[nav-container="container"]:data-[product="website-tenant-2"]:mt-2',
          'lg:[&_li:first-child]:data-[nav-container="container"]:data-[product="website-fdr"]:mt-2',
          'lg:[&_li:first-child]:data-[nav-container="container"]:data-[product="website-ffn"]:mt-2',
          'lg:[&_li:first-child]:data-[nav-container="container"]:data-[product="website-tenant-2"]:mt-2',
          'lg:[&_li:first-child]:data-[nav-container="container"]:data-[product="website-fdr"]:mt-2',
          'lg:[&_li:first-child]:data-[nav-container="container"]:data-[product="website-ffn"]:mt-2',
          'lg:[&_li:last-child]:data-[nav-container="container"]:data-[product="website-tenant-2"]:mt-2',
          'lg:[&_li:last-child]:data-[nav-container="container"]:data-[product="website-fdr"]:mt-2',
          'lg:[&_li:last-child]:data-[nav-container="container"]:data-[product="website-ffn"]:mt-2',
          'group'
        )}
        data-condensed={condensed}
        data-mobile-condensed={mobileCondensed}
        data-mobile-nav-open={mobileNavOpen}
        data-narrow={true}
        data-testid="acx-website-header"
        data-product={siteEnv}
        data-slug={params.slug}
      >
        {/* {showPhoneNumber && (
          <Head>
            <link rel="preload" href={phoneIconUrl} as="image" type="image/svg+xml" />
          </Head>
        )} */}
        <Container
          maxWidth={false}
          disableGutters
          className={classNames(
            'header-content-container',
            'max-w-[1440px] lg:pl-8 lg:pr-8',
            'data-[narrow=true]:max-w-[1280px]',
            'data-[narrow=true]:[div[page-main-variation="website-wfs"]_&]:max-w-[764px]'
          )}
          data-narrow={true}
        >
          {showDebtConsultantCta && !condensed && getDebtConsultantCTA()}
          <div
            id="mobile-header-bar-container"
            className={classNames(
              'flex flex-col overflow-x-hidden lg:items-center lg:justify-between lg:flex-row lg:h-24 lg:overflow-visible lg:transition-all lg:duration-[600ms_cubic-bezier(0.4,0,0.2,1)]',
              'max-lg:group-data-[mobile-nav-open=true]:transition-all max-lg:group-data-[mobile-nav-open=true]:duration-[600s] max-lg:group-data-[mobile-nav-open=true]:h-screen',
              isAchievePro &&
                'max-lg:group-data-[mobile-nav-open=true]:bg-white max-lg:group-data-[mobile-nav-open=true]:text-[#1D252F]',
              'lg:group-data-[mobile-nav-open=true]:h-24',
              'lg:group-data-[condensed=true]:h-16',
              'lg:group-data-[condensed=true]:group-data-[mobile-nav-open=true]:h-16'
            )}
          >
            {/* Container for content shown in the mobile header bar */}
            <div
              id="mobile-header-bar"
              className={classNames(
                siteEnv === AchievePage
                  ? isAchievePro
                    ? 'bg-white text-[#1D252F]'
                    : 'bg-white max-lg:group-data-[mobile-nav-open=true]:bg-black '
                  : 'bg-white items-center flex flex-row h-[72px] justify-between pl-4 pr-2 w-full z-2 transition-[height] duration-100 ease-linear delay-100 lg:relative lg:w-auto lg:p-0',
                siteEnv === AchievePage
                  ? 'items-center flex flex-row h-[72px] justify-between pl-4 pr-2 w-full z-2 [transition:background-color_400ms_cubic-bezier(0,0,0.2,1),height_400ms_cubic-bezier(0,0,0.2,1)] lg:relative lg:w-auto lg:p-0'
                  : 'max-lg:group-data-[mobile-nav-open=true]:h-[54px] group-data-[mobile-condensed=true]:h-[54px]',
                siteEnv !== AchievePage &&
                  'max-lg:group-data-[mobile-nav-open=true]:border-b-neutral-75 max-lg:group-data-[mobile-nav-open=true]:border-b max-lg:group-data-[mobile-nav-open=true]:border-solid max-lg:group-data-[mobile-nav-open=true]:border-t-0 max-lg:group-data-[mobile-nav-open=true]:border-x-0',
                'lg:[background:none] lg:group-data-[mobile-nav-open=true]:h-auto lg:group-data-[mobile-nav-open=true]:relative lg:group-data-[mobile-nav-open=true]:border-0',
                'data-[call-cta-mobile=true]:absolute data-[call-cta-mobile=true]:right-10',
                'max-lg:data-[call-cta-mobile=true]:group-data-[mobile-nav-open=true]:hidden',
                'lg:group-data-[open-button-call=true]:px-4 lg:group-data-[open-button-call=true]:py-2 lg:group-data-[open-button-call=true]:rounded-3xl',
                'lg:group-data-[open-button-call=false]:group-data-[condensed=false]:min-w-[42px] lg:group-data-[open-button-call=false]:group-data-[condensed=false]:min-h-[42px] lg:group-data-[open-button-call=false]:group-data-[condensed=false]:w-[42px] lg:group-data-[open-button-call=false]:group-data-[condensed=false]:h-[42px] lg:group-data-[open-button-call=false]:group-data-[condensed=false]:p-1',
                'lg:group-data-[open-button-call=true]:group-data-[condensed=false]:min-h-[42px] lg:group-data-[open-button-call=true]:group-data-[condensed=false]:h-[42px]',
                siteEnv === AchievePage &&
                  'max-lg:group-data-[mobile-nav-open=true]:h-[70px] group-data-[mobile-condensed=true]:h-[54px]'
              )}
            >
              {partnerLogo ? (
                <div className={'flex'}>
                  {AchieveLogoDesktop}
                  <div
                    className={classNames(
                      'flex items-center mx-4 my-0',
                      'group-data-[condensed=true]:mx-4 group-data-[condensed=true]:my-0',
                      'group-data-[condensed=false]:mx-4 group-data-[condensed=false]:my-0',
                      'group-data-[mobile-condensed=false]:mx-4 group-data-[mobile-condensed=false]:my-0',
                      'group-data-[mobile-condensed=true]:ml-[-60px] group-data-[mobile-condensed=true]:mr-0 group-data-[mobile-condensed=true]:my-0'
                    )}
                  >
                    <Plus height={30} width={30} />
                  </div>
                  <PartnerLogo src={partnerLogo?.file?.url} />
                </div>
              ) : (
                AchieveLogoDesktop
              )}
              {/* {!isAchievePro && <div className="contents lg:hidden">{AchieveLogoMobile}</div>} */}
              <div className="contents lg:hidden">{AchieveLogoMobile}</div>
              {/* Mobile nav menu open and close buttons */}
              <div className={'items-center flex h-[34px] justify-end relative w-full lg:hidden'}>
                {!mobileCondensed && showStickyFooter && !isAchievePro && (
                  <HeaderCta
                    siteEnv={siteEnv}
                    linkUrl={linkUrl}
                    linkText={linkText}
                    size="small"
                    className={classNames(
                      'mr-10',
                      siteEnv === AchievePage && 'max-lg:group-data-[mobile-nav-open=true]:hidden ',
                      {
                        'mr-[90px]': isMobile && phoneData?.phoneNumber,
                      }
                    )}
                    data-testid="header-get-started-button"
                  />
                )}
                {shouldShowPhoneNumber && (
                  <Box
                    className={classNames(
                      'group flex items-center cursor-pointer text-blue-500 gap-2 overflow-hidden transition-all duration-300 delay-150 z-2 p-2 rounded-3xl [justify-content:left] bg-neutral-50',
                      'group-data-[mobile-condensed=true]:h-[34px] group-data-[mobile-condensed=true]:p-1',
                      'group-data-[condensed=true]:h-[34px] group-data-[condensed=true]:p-1',
                      'group-data-[product="website-ffn"]:p-0 group-data-[product="website-ffn"]:rounded-none group-data-[product="website-ffn"]:[background:none]',
                      'group-data-[open-button-call=false]:group-data-[condensed=true]:w-[34px]'
                    )}
                    data-call-cta-mobile={true}
                    data-open-button-call={buttonCall}
                  >
                    <AchieveLink
                      className={'w-6 h-6 flex justify-center items-center'}
                      href={phoneHref}
                      track={{
                        list_name: 'PHONE CALL CLICK HEADER',
                        click_id: phoneData?.phoneNumber,
                        click_text: `${
                          mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
                        } | ${phoneData?.phoneNumber}`,
                        click_url: phoneData?.phoneNumber,
                        click_type: 'Phone Click',
                        event_action: 'button_click',
                        nav_link_section: 'Header',
                        track_event: 'global_header',
                      }}
                      data-testid="mobile-phone-number-link"
                    >
                      {getMobileIcon()}
                    </AchieveLink>
                  </Box>
                )}
                <>
                  <button
                    onClick={handleNavOpen}
                    className={classNames(
                      'items-center text-neutral-900 flex absolute transition-[400ms_cubic-bezier(0,0,0.2,1)] px-2 py-[11px] border-0 [background:none]',
                      'max-lg:group-data-[mobile-nav-open=true]:-translate-y-20',
                      'lg:hidden',
                      !showNav && 'hidden'
                    )}
                    data-testid="mobile-nav-open-button"
                    aria-label="Main navigation menu button"
                  >
                    <MenuIcon />
                  </button>
                  <button
                    onClick={handleNavClose}
                    role="button"
                    className={classNames(
                      'items-center text-neutral-900 flex absolute transition-[400ms_cubic-bezier(0,0,0.2,1)] px-2 py-[11px] border-0 [background:none]',
                      '[&_span]:no-underline [&_span]:mr-3',
                      'group-data-[mobile-nav-open=false]:translate-x-[200px]',
                      'lg:hidden',
                      !showNav && 'hidden'
                    )}
                    data-testid="mobile-nav-close-button"
                    aria-label="Main navigation menu close button"
                    id="mobile-nav-close-button"
                  >
                    <Close
                      title="Close button"
                      className="[&_path]:fill-white!"
                      height={20}
                      width={20}
                    />
                  </button>
                  {siteEnv === AchievePage ? (
                    <button
                      onClick={handleNavClose}
                      role="button"
                      className={classNames(
                        'items-center text-neutral-900 flex absolute transition-[400ms_cubic-bezier(0,0,0.2,1)] px-2 py-[11px] border-0 [background:none]',
                        '[&_span]:no-underline [&_span]:mr-3',
                        'group-data-[mobile-nav-open=false]:translate-x-[200px]',
                        'lg:hidden',
                        !showNav && 'hidden'
                      )}
                      data-testid="mobile-nav-close-button"
                      aria-label="Main navigation menu close button"
                      id="mobile-nav-close-button"
                    >
                      <Close
                        title="Close button"
                        className={
                          isAchievePro ? '[&_path]:fill-[#1D252F]!' : '[&_path]:fill-white!'
                        }
                        height={20}
                        width={20}
                      />
                    </button>
                  ) : (
                    <button
                      onClick={handleNavClose}
                      role="button"
                      className={classNames(
                        'items-center text-neutral-900 flex absolute transition-[400ms_cubic-bezier(0,0,0.2,1)] px-2 py-[11px] border-0 [background:none]',
                        '[&_span]:no-underline [&_span]:mr-3',
                        'group-data-[mobile-nav-open=false]:translate-x-[200px]',
                        'lg:hidden',
                        !showNav && 'hidden'
                      )}
                      data-testid="mobile-nav-close-button"
                      aria-label="Main navigation menu close button"
                      id="mobile-nav-close-button"
                    >
                      <Typography variant="bodyXs" fontWeight="medium" component="span">
                        CLOSE
                      </Typography>
                      <Close title="Close button" />
                    </button>
                  )}
                </>
              </div>
            </div>
            {/* Main nav and sub nav list components */}
            {((showNav && !showStickyFooter) || (showNav && showStickyFooter && !condensed)) && (
              <Navigation
                siteEnv={siteEnv}
                event={linkEvent}
                mobileNavOpen={mobileNavOpen}
                items={mainNavigation}
                disabledRoutes={disabledRoutes}
                disabledElements={disabledElements}
                mobileSubnav={getSubNav()}
                {...(siteEnv === FdrPage ? { titleVariant: 'bodySm' } : {})}
                className={
                  isAchievePro
                    ? 'lg:w-1/2! max-lg:bg-white! max-lg:[&_ul_*]:text-[#1D252F]!'
                    : undefined
                }
              />
            )}
            {/* Right Panel */}
            <div
              id="mobile-header-right-panel"
              className={classNames(
                'group',
                'flex items-center absolute transition-[400ms_cubic-bezier(0,0,0.2,1)] z-2 gap-4 overflow-hidden translate-y-0 top-2.5 lg:relative lg:min-w-[280px] lg:max-w-[280px] lg:overflow-hidden lg:justify-end lg:right-[initial] lg:top-[initial]',
                '[&_button]:transition-[400ms_cubic-bezier(0,0,0.2,1)]',
                'data-[none-phone-number=true]:right-[50px]',
                'data-[none-phone-number=false]:justify-end',
                'max-lg:group-data-[mobile-nav-open=true]:translate-y-[-70px] max-lg:group-data-[mobile-nav-open=true]:transition-all max-lg:group-data-[mobile-nav-open=true]:duration-[400ms_cubic-bezier(0,0,0.2,1)]',
                'group-data-[mobile-condensed=false]:translate-y-[-70px] group-data-[mobile-condensed=false]:transition-all group-data-[mobile-condensed=false]:duration-[400ms_cubic-bezier(0,0,0.2,1)]',
                'lg:group-data-[mobile-condensed=false]:flex lg:group-data-[mobile-condensed=false]:translate-y-0',
                // Achieve Pro unclips the panel so the out-of-flow call button can sit outside it
                isAchievePro && 'flex-row-reverse lg:overflow-visible!',
                {
                  'max-w-max lg:max-w-max': showStickyFooter || siteEnv === FdrPage,
                  'right-[95px]': shouldShowPhoneNumber,
                  'right-[50px]': !shouldShowPhoneNumber,
                }
              )}
              data-open-button-call={buttonCall}
              data-none-phone-number={phoneData?.phoneNumber ? false : true}
            >
              {!shouldShowPhoneNumber && isDebtConsolidation && (
                <Box className={'lg:[&_p]:font-[bold] lg:[&_span]:text-blue-800'}>
                  <AchieveLink
                    href={phoneHref}
                    track={{
                      list_name: 'PHONE CALL CLICK HEADER',
                      click_id: phoneData?.phoneNumber,
                      click_text: `${
                        mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
                      } | ${phoneData?.phoneNumber}`,
                      click_url: phoneData?.phoneNumber,
                      click_type: 'Phone Click',
                      event_action: 'button_click',
                      nav_link_section: 'Header',
                      track_event: 'global_header',
                    }}
                    data-testid="mobile-phone-number-link"
                  >
                    {getCallDesktop()}
                  </AchieveLink>
                </Box>
              )}
              {phoneData?.phoneNumber &&
                !isMobile &&
                showPhoneNumber &&
                !condensed &&
                showStickyFooter && (
                  <Box
                    className={classNames(
                      'flex items-center cursor-pointer text-blue-500 gap-2 overflow-hidden transition-all duration-300 delay-150 z-2 p-2 rounded-3xl [justify-content:left] bg-neutral-50',
                      'group-data-[mobile-condensed=true]:h-[34px] group-data-[mobile-condensed=true]:p-1',
                      'group-data-[condensed=true]:h-[34px] group-data-[condensed=true]:p-1',
                      'group-data-[product="website-ffn"]:p-0 group-data-[product="website-ffn"]:rounded-none group-data-[product="website-ffn"]:[background:none]',
                      'group-data-[open-button-call=false]:group-data-[condensed=true]:w-[34px]'
                    )}
                  >
                    <Box
                      className={classNames(
                        'flex flex-row items-center transition-all duration-300',
                        'data-[open-button-call=false]:pl-[12%]'
                      )}
                    >
                      <span
                        role="button"
                        aria-label="Toggle phone number"
                        onClick={handleCall}
                        className="flex items-center cursor-pointer"
                      >
                        {getMobileIcon()}
                      </span>
                      <Box
                        className={classNames(
                          'w-[125px] flex justify-center opacity-0 delay-[0ms] transition-opacity text-nowrap',
                          '[&_span]:leading-[100%]',
                          'group-data-[open-button-call=true]:opacity-100 group-data-[open-button-call=true]:delay-310'
                        )}
                      >
                        <AchieveLink
                          href={phoneHref}
                          track={{
                            list_name: 'PHONE CALL CLICK HEADER',
                            click_id: phoneData?.phoneNumber,
                            click_text: `${
                              mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
                            } | ${phoneData?.phoneNumber}`,
                            click_url: phoneData?.phoneNumber,
                            click_type: 'Phone Click',
                            event_action: 'button_click',
                            nav_link_section: 'Header',
                            track_event: 'global_header',
                          }}
                          data-testid="desktop-phone-number-link"
                        >
                          <Typography
                            variant="bodySm"
                            fontWeight="regular"
                            onClick={callToAssociatedNumber}
                            data-testid="desktop-phone-number"
                          >
                            {phoneData?.phoneNumber}
                          </Typography>
                        </AchieveLink>
                      </Box>
                      <CloseIcon
                        className={''}
                        width={18}
                        height={18}
                        color={AchieveTheme?.ascend?.colors?.neutral?.[150]}
                        onClick={handleCall}
                      />
                    </Box>
                  </Box>
                )}
              {condensed && showStickyFooter && phoneData?.phoneNumber && (
                <div className="flex flex-row gap-2 hidden lg:block">
                  <AchieveLink
                    href={phoneHref}
                    track={{
                      list_name: 'CALL STICKY HEADER',
                      click_id: phoneData?.phoneNumber,
                      click_text: `Call us`,
                      click_url: phoneData?.phoneNumber,
                      click_type: 'Call us Click',
                      event_action: 'button_click',
                      nav_link_section: 'Sticky Header',
                      track_event: 'call-sticky- hidden lg:block',
                    }}
                    data-testid="call-sticky- hidden lg:block"
                  >
                    {callUsButton}
                  </AchieveLink>
                </div>
              )}
              {siteEnv === FdrPage &&
                ((showSignIn && !showStickyFooter) ||
                  (showSignIn && showStickyFooter && !condensed)) && (
                  <SignInLink
                    siteEnv={siteEnv}
                    signInLinkUrl={signInLinkUrl}
                    signInLinkText={signInLinkText}
                    event={signInEvent}
                    className={'hidden lg:inline-block lg:min-w-[60px] hover:text-blue-500'}
                    data-testid="header-sign-in-link-fdr"
                    disabledRoutes={disabledRoutes}
                    underline="hover"
                    outlined
                    textClassName="text-[13px] leading-[150%] font-bold whitespace-nowrap"
                    onClick={(e) => {
                      openOverlayInsteadOfNavigating(e)
                      handleButtonClick(signInLinkUrl, 'Sign In', false)
                    }}
                  />
                )}

              {/* "Get Started" CTA button is always present in desktop and is hidden and shown on
                scroll in mobile. */}
              {headerCta && (
                <HeaderCta
                  siteEnv={siteEnv}
                  linkUrl={linkUrl}
                  linkText={linkText}
                  size="small"
                  data-condensed={isMobile || condensed}
                  className={classNames(
                    'inline-block opacity-0 delay-[0ms] transition-opacity absolute w-0 hover:no-underline text-nowrap',
                    '[&_button]:transition-all [&_button]:duration-[600msd_cubic-bezier(0.4,0,0.2,1)] [&_button]:z-1',
                    '[&>button]:hover:text-blue-500 [&>button]:hover:bg-blue-200',
                    'group-data-[open-button-call=false]:opacity-100 group-data-[open-button-call=false]:w-auto group-data-[open-button-call=false]:relative',
                    {
                      'hidden lg:block': showStickyFooter,
                    },
                    {
                      '[&_button]:group-data-[condensed=false]:px-[10px] [&_button]:group-data-[condensed=false]:py-1':
                        siteEnv === FdrPage,
                    },
                    {
                      '[&_button]:group-data-[condensed=false]:px-4 [&_button]:group-data-[condensed=false]:py-2':
                        siteEnv !== FdrPage,
                    }
                  )}
                  data-testid="header-get-started-button"
                  condensed={condensed}
                  textClassName={classNames({
                    'text-[13px] leading-[150%] font-bold': siteEnv === FdrPage,
                  })}
                />
              )}
              {siteEnv === AchievePage &&
                ((showSignIn && !showStickyFooter) ||
                  (showSignIn && showStickyFooter && !condensed)) && (
                  <SignInLink
                    siteEnv={siteEnv}
                    signInLinkUrl={signInLinkUrl}
                    signInLinkText={signInLinkText}
                    event={signInEvent}
                    className={classNames(
                      // order-first puts Partner Login right of Partner with us in the reversed row
                      isAchievePro
                        ? 'hover:text-black text-black order-first'
                        : 'hover:text-blue-500',
                      'hidden lg:inline-block lg:min-w-[60px] '
                    )}
                    data-testid="header-sign-in-link"
                    disabledRoutes={disabledRoutes}
                    underline="hover"
                    outlined={siteEnv === FdrPage}
                    textClassName={classNames({
                      'text-[13px] leading-[150%] font-bold whitespace-nowrap': siteEnv === FdrPage,
                    })}
                  />
                )}
              {/* Positioned out of flow so the Partner CTAs keep their original placement */}
              {isAchievePro && condensed && (
                <div className="hidden lg:block absolute right-full top-1/2 -translate-y-1/2 mr-6">
                  <Button
                    component="a"
                    href="tel:+18336491847"
                    color="secondary"
                    variant="contained"
                    size="small"
                    className="flex items-center gap-[6px] self-stretch whitespace-nowrap no-underline text-[#154199]! **:text-[#154199]! group-data-[condensed=false]:px-4 group-data-[condensed=false]:py-2"
                    data-testid="header-achieve-pro-call-button"
                  >
                    <PhoneCall size={16} />
                    <Typography variant="bodySm" className="font-extrabold!" fontWeight="extrabold">
                      833-649-1847
                    </Typography>
                  </Button>
                </div>
              )}
              {condensed && showStickyFooter && (
                <div className="flex flex-row gap-2 hidden lg:block">
                  <div className="flex flex-row gap-2 items-center justify-center">
                    <div className="w-[165px] h-[20px]">
                      <MediaImageStandard content={tp_image} />
                    </div>
                    <OwnerTypography variant="bodyXs" className="text-black" content={tp_text} />
                  </div>
                </div>
              )}
            </div>
            {/* Action bar that shows at the bottom of the mobile nav */}
            {isMobile && siteEnv !== AchievePage && (
              <HeaderMobileActions
                siteEnv={siteEnv}
                disabledRoutes={disabledRoutes}
                headerCta={headerCta}
                signInLinkText={signInLinkText}
                signInLinkUrl={signInLinkUrl}
                getStartedLinkUrl={linkUrl}
                getStartedLinkText={linkText}
              />
            )}
          </div>
        </Container>
        {condensed && showProgressPage && (
          <div
            className={classNames(
              'w-full h-[3px] absolute',
              '[background:linear-gradient(to_bottom,rgb(0_0_0_/_20%),rgb(0_0_0_/_0%),rgb(0_0_0_/_0%));]',
              '-z-10'
            )}
          >
            <div
              className={'content-[""] absolute h-[3px] bg-blue-400'}
              style={{ width: `${progressPage}%` }}
            />
          </div>
        )}
      </header>
    </>
  )
}
